import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Spinner, Alert, Form } from 'react-bootstrap';
import { FaEdit, FaTrash, FaPlus, FaArrowLeft, FaArrowRight } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import '../../../assets/css/Product/ProductList.css';

const API_BASE_URL = process.env.REACT_APP_API_URL;

const ProductList = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]); // For filtered display
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [showDescModal, setShowDescModal] = useState(false);
  const [descModalContent, setDescModalContent] = useState('');
  const [descModalTitle, setDescModalTitle] = useState('');
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState(''); // Search state
  const navigate = useNavigate();

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // Permissions
  const userPermissions = JSON.parse(localStorage.getItem("permissions") || "[]");
  const canCreate = userPermissions.includes("create_Product");
  const canEdit = userPermissions.includes("update_Product");
  const canDelete = userPermissions.includes("delete_Product");
  const canToggleStatus = userPermissions.includes("update_Product");

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_BASE_URL}/api/products`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setProducts(response.data);
        setFilteredProducts(response.data);
      } catch (err) {
        console.error(err);
        setError('Failed to fetch products');
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  // Filter products whenever searchTerm or products change
  useEffect(() => {
    const lowerSearch = searchTerm.toLowerCase();
    const filtered = products.filter(p =>
      (p.name && p.name.toLowerCase().includes(lowerSearch)) ||
      (p.category?.name && p.category.name.toLowerCase().includes(lowerSearch))
    );
    setFilteredProducts(filtered);
    setCurrentPage(1); // Reset to first page on filter change
  }, [searchTerm, products]);

  const getTextSnippet = (html, wordLimit = 10) => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    const text = tempDiv.textContent || tempDiv.innerText || '';
    const words = text.split(/\s+/).filter(Boolean);
    if (words.length <= wordLimit) return text;
    return words.slice(0, wordLimit).join(' ') + '...';
  };

  const handleDeleteClick = (product) => {
    setProductToDelete(product);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/api/products/${productToDelete._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProducts((prev) => prev.filter((p) => p._id !== productToDelete._id));
      setShowDeleteModal(false);
      setProductToDelete(null);
    } catch (err) {
      setError('Failed to delete product');
      console.error(err);
    }
  };

  const handleAddProduct = () => {
    if (canCreate) {
      navigate('/Addproduct');
    }
  };

  const openDescriptionModal = (product) => {
    setDescModalContent(product.productDescription);
    setDescModalTitle(product.name);
    setShowDescModal(true);
  };

  const handleEditClick = (product) => {
    if (canEdit) {
      navigate(`/editproduct/${product._id}`);
    }
  };

  const handleStatusToggle = async (product) => {
    if (!canToggleStatus) return;
    try {
      setStatusUpdatingId(product._id);
      const token = localStorage.getItem('token');
      const updatedStatus = !product.status;
      await axios.put(
        `${API_BASE_URL}/api/products/${product._id}/status`,
        { status: updatedStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProducts((prev) =>
        prev.map((p) => (p._id === product._id ? { ...p, status: updatedStatus } : p))
      );
    } catch (err) {
      console.error('Status toggle failed:', err);
      setError('Failed to toggle product status');
    } finally {
      setStatusUpdatingId(null);
    }
  };

  // Pagination calculations
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedProducts = filteredProducts.slice(startIndex, startIndex + itemsPerPage);

  const handlePrev = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const handleNext = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  if (loading)
    return (
      <div className="text-center my-5">
        <Spinner animation="border" variant="primary" /> Loading products...
      </div>
    );

  if (error) return <Alert variant="danger">{error}</Alert>;

  return (
    <div className="product-list-container p-4">
      {/* Heading on top */}
      <h3 className="fw-bold heding mb-3">Product List</h3>

      {/* Search bar and Add button below heading */}
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
        <Form.Control
          type="text"
          placeholder="🔍 Search by product or category..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ maxWidth: '350px' }}
        />
        <Button
          className="add-product-btn shadow-sm"
          onClick={handleAddProduct}
          disabled={!canCreate}
          title={!canCreate ? "You do not have permission to add products" : ""}
        >
          <FaPlus className="me-2" />
          Add Product
        </Button>
      </div>

      <Table striped bordered hover responsive className="shadow-sm">
        <thead className="table-primary">
          <tr>
            <th>No.</th>
            <th>Category</th>
            <th>Name</th>
            <th>Description</th>
            <th>Remarks</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {paginatedProducts.length === 0 && (
            <tr>
              <td colSpan="7" className="text-center text-muted">
                No products found.
              </td>
            </tr>
          )}
          {paginatedProducts.map((product, i) => (
            <tr key={product._id}>
              <td>{startIndex + i + 1}</td>
              <td>{product.category?.name || '-'}</td>
              <td>{product.name || '-'}</td>
              <td>
                {product.productDescription ? (
                  <>
                    <div>{getTextSnippet(product.productDescription, 5)}</div>
                    <div
                      className="read-more-link text-primary"
                      role="button"
                      onClick={() => openDescriptionModal(product)}
                    >
                      Click to read more
                    </div>
                  </>
                ) : (
                  '-'
                )}
              </td>
              <td>{product.remarks || '-'}</td>
              <td className="text-center">
                <Form.Check
                  type="switch"
                  checked={product.status}
                  onChange={() => handleStatusToggle(product)}
                  disabled={!canToggleStatus || statusUpdatingId === product._id}
                  title={!canToggleStatus ? "You do not have permission to update status" : ""}
                />
              </td>
              <td>
                <Button
                  variant="outline-primary"
                  size="sm"
                  className="me-2"
                  onClick={() => handleEditClick(product)}
                  disabled={!canEdit}
                  title={!canEdit ? "You do not have permission to edit" : ""}
                >
                  <FaEdit />
                </Button>
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => handleDeleteClick(product)}
                  disabled={!canDelete}
                  title={!canDelete ? "You do not have permission to delete" : ""}
                >
                  <FaTrash />
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {/* Updated: Pagination Right Aligned */}
      {totalPages > 1 && (
        <div className="d-flex justify-content-end align-items-center gap-3 mt-3">
          <Button
            variant="secondary"
            onClick={handlePrev}
            disabled={currentPage === 1}
          >
            <FaArrowLeft className="me-1" /> Previous
          </Button>

          <span>
            Page {currentPage} of {totalPages}
          </span>

          <Button
            variant="secondary"
            onClick={handleNext}
            disabled={currentPage === totalPages}
          >
            Next <FaArrowRight className="ms-1" />
          </Button>
        </div>
      )}

      {/* Delete Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete <strong>{productToDelete?.name}</strong>?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Description Modal */}
      <Modal show={showDescModal} onHide={() => setShowDescModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{descModalTitle} - Full Description</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div dangerouslySetInnerHTML={{ __html: descModalContent }} />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDescModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ProductList;
